(ns capra.adapters.sente
  "Sente WebSocket adapter for capra client.

   Uses Sente's built-in request-reply for pipeline calls.
   Listens for server-initiated push events for callbacks."
  (:require
   [capra.transport :as transport]
   [taoensso.sente :as sente]))

;; =============================================================================
;; Constants
;; =============================================================================

(def ^:private max-send-retries 3)
(def ^:private send-retry-base-ms 200)
(def ^:private send-timeout-ms 30000)

;; =============================================================================
;; Retry Logic
;; =============================================================================

(defn- send-with-retry
  "Send a pipeline message via Sente, retrying on :chsk/closed.
   Sente returns :chsk/closed when the socket is transitioning (e.g.
   WS → AJAX downgrade). Retrying with backoff lets the connection
   stabilize before giving up."
  [transport pipeline-msg attempt]
  (js/Promise.
   (fn [resolve reject]
     (if-let [send-fn @(:send-fn-atom transport)]
       (send-fn [:capra/message pipeline-msg] send-timeout-ms
                (fn [reply]
                  (cond
                    (= :chsk/timeout reply)
                    (reject (ex-info "RPC call timed out" {:qid (:qid pipeline-msg)}))

                    (and (map? reply) (= :rpc/return (:type reply)))
                    (resolve reply)

                    (and (map? reply) (= :rpc/error (:type reply)))
                    (reject (ex-info (:message reply) reply))

                    ;; :chsk/closed during WS→AJAX transition — retry
                    (and (= :chsk/closed reply) (< attempt max-send-retries))
                    (let [delay (* send-retry-base-ms (js/Math.pow 2 attempt))]
                      (js/setTimeout
                       (fn []
                         (-> (send-with-retry transport pipeline-msg (inc attempt))
                             (.then resolve)
                             (.catch reject)))
                       delay))

                    :else
                    (reject (ex-info "Unexpected reply" {:raw reply})))))
       (reject (ex-info "Not connected" {}))))))

;; =============================================================================
;; Sente Transport
;; =============================================================================

(defrecord SenteClientTransport
           [chsk-atom send-fn-atom push-handler-atom welcome-handler-atom reconnect-handler-atom]

  transport/ICapraTransport

  (send-pipeline! [this pipeline-msg]
    (send-with-retry this pipeline-msg 0))

  (send-release! [_this cap-ids fn-ids]
    (when-let [send-fn @send-fn-atom]
      ;; Fire and forget — no reply needed
      (send-fn [:capra/message {:type :rpc/release
                                :caps (or cap-ids #{})
                                :fns  (or fn-ids #{})}])))

  (on-push! [_this handler-fn]
    (reset! push-handler-atom handler-fn))

  (on-welcome! [_this handler-fn]
    (reset! welcome-handler-atom handler-fn))

  (on-reconnect! [_this handler-fn]
    (reset! reconnect-handler-atom handler-fn))

  (close! [_this]
    (reset! push-handler-atom nil)
    (reset! welcome-handler-atom nil)
    (reset! reconnect-handler-atom nil)))

;; =============================================================================
;; Event Router
;; =============================================================================

(defn- route-event!
  "Route server-initiated Sente events to capra handlers."
  [transport {:keys [id ?data]}]
  (case id
    ;; Server push — callback invocations
    :capra/push
    (when-let [handler @(:push-handler-atom transport)]
      (handler ?data))

    ;; Welcome message on connect
    :capra/welcome
    (when-let [handler @(:welcome-handler-atom transport)]
      (handler ?data))

    ;; Connection state changes
    :chsk/state
    (let [new-state (second ?data)]
      (when (and (:open? new-state)
                 (not (:first-open? new-state)))
        ;; Reconnect (not first connect)
        (when-let [handler @(:reconnect-handler-atom transport)]
          (handler))))

    ;; Ignore all other events
    nil))

;; =============================================================================
;; Public API
;; =============================================================================

(defn make-sente-transport
  "Create a capra transport backed by a Sente channel socket.

   sente-opts: {:send-fn  - Sente send function
                :ch-recv  - Sente receive channel}"
  [{:keys [send-fn ch-recv] :as sente-opts}]
  (let [t (->SenteClientTransport
           (atom sente-opts)
           (atom send-fn)
           (atom nil)   ;; push handler
           (atom nil)   ;; welcome handler
           (atom nil))] ;; reconnect handler
    ;; Start event routing for server-initiated events only
    (sente/start-client-chsk-router!
     ch-recv
     (partial route-event! t))
    t))
